/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {Select} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useWindowSize} from '../hooks/useWindowSize';
import {getMenuStyles} from './FilterHelpers';

/**
 * Defines the prop definitions for custom styles ...
 *
 * menuOpen   : If true, menu is open else not.
 * offsetLeft : The x position of more filters wrapper.
 */
export type UseStylesProps = {
  menuOpen?: boolean;
  offsetLeft?: number;
  width?: number;
  containerWidth?: number;
  isTransparent?: boolean;
  menuWidth?: string;
  menuLeft?: string;
  menuTop?: string;
};

/**
 * Defines the prop definitions for MoreFilters ...
 *
 * show    : If true, MoreFilters is rendered else not.
 * options : The filters shown on expanding MoreFilters.
 * count   : The selected filters count.
 */
export type MoreFiltersProps = {
  show: boolean;
  options?: Array<any>;
  count?: number;
  filterState?: any;
  showMoreFilter?: (name: string) => boolean;
  width?: number;
  accountId?: number;
  accountIds: number[];
  containerWidth?: number;
  menuWidth?: string;
  isTransparent?: boolean;
  ancestorAccountIds?: number[];
  descendantAccountId?: number[];
  commonProps?: GenericObject;
};

// defines the styles used by MoreFilters ...
const useStyles = makeStyles(() => ({
  moreFiltersWrapper : {
    width                  : ({width}: UseStylesProps) => `${width}px`,
    padding                : '8px 4px',
    backgroundColor        : 'transparent !important',
    '& .MuiInputBase-root' : {
      width           : '100%',
      backgroundColor : ({isTransparent}: UseStylesProps) => isTransparent ? 'transparent !important' : dlsColor('white'),
      padding         : '4px 0px 4px 0px !important'
    },
    '& .MuiInput-underline' : {
      borderBottom                 : `solid 1px ${dlsColor('grey5')}`,
      '&:hover:not(.Mui-disabled)' : {
        '&:before' : {
          marginBottom : '-1px',
          borderBottom : ({menuOpen}: UseStylesProps) =>
            menuOpen ? `solid 1px ${dlsColor('caBlue')}` : 'none'
        }
      },
      '&:before' : {
        marginBottom : '-1px',
        borderBottom : ({menuOpen}: UseStylesProps) =>
          menuOpen ? `solid 1px ${dlsColor('caBlue')}` : 'none'
      },
      '&:after' : {
        borderBottom : 'none'
      }
    },
    '& .MuiInput-root' : {
      cursor          : 'pointer',
      height          : '49px',
      display         : 'flex',
      padding         : '10px !important',
      alignItems      : 'center',
      justifyContent  : 'space-between',
      maxWidth        : 'initial !important',
      backgroundColor : dlsColor('white')
    }
  },
  root : {
    cursor : 'pointer'
  },
  paper : {
    overflow  : 'unset',
    top       : '125px !important',
    maxWidth  : '180px',
    '& #menu' : {
      top   : ({menuTop}: UseStylesProps) => menuTop,
      left  : ({offsetLeft, menuLeft}: UseStylesProps) => menuLeft || `calc(100% - ${offsetLeft + 240}px)`,
      width : ({containerWidth, menuWidth}: UseStylesProps) => menuWidth || `${containerWidth}px`
    },
    '& .MuiInput-underline' : {
      borderBottom : 'none !important'
    },
    '& .MuiSelect-icon' : {
      display : 'none'
    },
    '& .MuiSelect-selectMenu span' : {
      fontWeight    : 'normal',
      textTransform : 'initial'
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled)' : {
      backgroundColor : dlsColor('paleBlue')
    },
    '& .MuiList-root' : {
      paddingBottom : '0px'
    },
    '& .MuiSelect-select.Mui-disabled' : {
      opacity : '0.6'
    },
    '& .MuiSelect-root [data-title=title] span' : {
      display : 'inline !important'
    }
  },
  selectRoot : {
    color         : dlsColor('grey3'),
    textTransform : 'uppercase',
    fontWeight    : 700,
    letterSpacing : '1px',
    fontSize      : '14px',
    lineHeight    : '18px',
    width         : '100%',
    minHeight     : '38px',
    paddingTop    : '0px',
    paddingBottom : '0px',
    display       : 'flex',
    alignItems    : 'center',
    minWidth      : 'initial',
    paddingLeft   : 0,

    '&:focus' : {
      backgroundColor : 'transparent'
    },
    '&.MuiSelect-root [data-title=title]' : {
      overflow     : 'hidden',
      maxWidth     : '150px',
      whiteSpace   : 'pre',
      textOverflow : 'ellipsis',
      '& span'     : {
        display : 'inline'
      }
    }
  },
  count : {
    color   : dlsColor('grey4'),
    padding : '0px 10px 0px 5px'
  },
  selectIcon : {
    transform : ({menuOpen}: UseStylesProps) =>
      `rotate(${menuOpen ? '180deg' : '0deg'})`,
    transition : 'transform 300ms ease-in-out'
  }
}));

/**
 * The MoreFilters component manages rendering the individual filters with a
 * custom value which can be modified to limit the filters which are to be
 * rendered as more filters ...
 *
 * ```typescript
 *
 * import {MoreFilters} from '@ion/dls/src/filters';
 *
 * <MoreFilters show={show}
 *   filterState={filterState}
 *   props={props}
 *   count={count} />
 *
 * ```
 */
export const MoreFilters = forwardRef(({
  show,
  options,
  count,
  filterState,
  showMoreFilter,
  accountId,
  accountIds,
  containerWidth,
  isTransparent,
  width = 200,
  descendantAccountId,
  ancestorAccountIds,
  commonProps = {}
}: MoreFiltersProps, ref) => {
  const [open, setOpen]       = useState(false);
  const [filters, setFilters] = useState([]);
  const property              = 'left';
  const elementId             = 'moreFiltersWrapper';
  const value                 = 'offsetLeft';
  const offsetLeft            = document.getElementById(elementId)?.[value];
  let classes                 = useStyles({
    menuOpen   : open,
    offsetLeft : (containerWidth ?? offsetLeft)
  });
  const dimension             = useWindowSize(300, {
    elementId, property, value
  });

  const {
    menuWidth,
    menuLeft,
    menuTop
  } = getMenuStyles(false, true);

  classes = useStyles({
    menuOpen   : open,
    offsetLeft : containerWidth ?? dimension?.[property],
    isTransparent,
    containerWidth,
    menuWidth,
    menuLeft,
    menuTop,
    width
  });

  useEffect(() => {
    if (filterState) {
      const items = options.map(option => {
        if (!isNil(option)) {
          const {name} = option.props || {};
          return ({
            ...option,
            ...(name ? {
              props : {
                ...option.props,
                filterState : filterState?.[name],
                account     : accountId,
                accountId,
                accountIds,
                descendantAccountId,
                ancestorAccountIds,
                ...commonProps
              }
            } : {})
          });
        }
      });
      setFilters(items);
    }
  }, [filterState, commonProps, options, accountId, accountIds]);

  /**
   * @private
   */
  const toggle = function(open = false) {
    return function() {
      setOpen(open);
    };
  };

  /**
   * Returns a reference to the more filters api ...
   */
  useImperativeHandle(ref, () => ({
    handleClose : toggle()
  }));

  if (show) {
    return (
      <div className={classes.moreFiltersWrapper} id='moreFiltersWrapper'>
        <Select open={open}
          onClose={toggle()}
          onOpen={toggle(true)}
          MenuProps={{
            PopoverClasses : {
              root  : classes.root,
              paper : classes.paper
            }

          }}
          classes={{
            root : classes.selectRoot,
            icon : classes.selectIcon
          }}
          value={translate('common:filters.moreFilters')}
          renderValue={function() {
            return (
              <div data-title='title'>
                <span>{translate('common:filters.moreFilters')}</span>
                {count > 0 &&
                <span className={classes.count}>
                  {`(${count})`}
                </span>
                }
              </div>
            );
          }}>
          {filters?.map(filter => {
            if (showMoreFilter?.(filter?.props?.name) ?? true) {
              return filter;
            }
            return null;
          })}
        </Select>
      </div>
    );
  }
  return null;
});

export default MoreFilters;
